import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import { apiRequest } from "../utils/api";
import { getDate } from "../utils/date";
import Swal from "sweetalert2";
import { 
  HiDocumentText, 
  HiClipboardDocumentList, 
  HiArrowPath, 
  HiTrash,
  HiInbox,
  HiCloudArrowDown,
  HiDocumentDuplicate,
  HiScissors,
  HiArrowUpOnSquareStack,
  HiRocketLaunch
} from "react-icons/hi2";

// Status color helper
function statusColor(status) {
  if (status === "completed") return "#27ae60";
  if (status === "started")   return "#2980b9";
  if (status === "failed")    return "#e74c3c";
  return "#888";
}

// Stage badge label
function stageLabel(stage) {
  const map = {
    download:           "⬇ Download",
    unzip:              "📦 Unzip",
    chunk_new_style:    "✂ Chunk New Style",
    chunk_prefix:       "✂ Chunk Prefix",
    chunk_dateless:     "✂ Chunk Dateless",
    truncate_clones:    "🗑 Truncate Clones",
    delete_zip:         "🗑 Delete ZIP",
    delete_all_files:   "🗑 Delete All Files",
  };
  if (stage?.startsWith("bulk_load_")) return `📤 Bulk Load ${stage.replace("bulk_load_", "")}`;
  if (stage?.startsWith("load_"))      return `📥 ${stage.replace("load_", "")}`;
  return map[stage] || stage;
}

function Dvla() {
  const [history, setHistory]         = useState([]);
  const [linesPerFile, setLinesPerFile] = useState(1000);
  const [loading, setLoading]         = useState(false);

  // Live log viewer state
  const [logOpen, setLogOpen]         = useState(false);
  const [logJobId, setLogJobId]       = useState(null);
  const [logJobName, setLogJobName]   = useState("");
  const [logRows, setLogRows]         = useState([]);
  const [logPolling, setLogPolling]   = useState(false);
  const logEndRef   = useRef(null);
  const pollRef     = useRef(null);

  // ─── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      name: "ID",
      selector: (row) => <>{row.id}</>,
      sortable: true,
      width: "70px",
    },
    {
      name: "File Name",
      selector: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <HiDocumentText style={{ fontSize: "18px", color: "#64748b" }} />
          <span style={{ fontWeight: 500, color: "#1e293b", fontSize: "14px" }}>
            {row.filename?.substring(0, 35)}{row.filename?.length > 35 ? "..." : ""}
          </span>
        </div>
      ),
      grow: 2,
    },
    {
      name: "Download",
      selector: (row) => (
        <p style={{ textTransform: "capitalize", margin: 0 }}
          className={row?.status === "completed" ? "text-success" : row?.status === "downloading" ? "text-primary" : "text-danger"}>
          {row.status}
        </p>
      ),
    },
    {
      name: "Pipeline",
      selector: (row) => (
        <p style={{ textTransform: "capitalize", margin: 0 }}
          className={row?.extracted_status === "completed" ? "text-success" : row?.extracted_status === "extracting" ? "text-warning" : row?.extracted_status === "failed" ? "text-danger" : "text-secondary"}>
          {row.extracted_status}
        </p>
      ),
    },
    {
      name: "Size",
      selector: (row) => <p>{(row.file_size / (1024 * 1024)).toFixed(2)} MB</p>,
    },
    {
      name: "Date",
      selector: (row) => <p>{getDate(row.created_at)}</p>,
    },
    {
      name: "New Style",
      selector: (row) => (
        <div style={{ margin: 0, lineHeight: 1.5 }}>
          <div>{row.new_style_count ?? "—"} chunks</div>
          {row.new_style_loaded > 0 && <div className="text-success" style={{ fontSize: 11 }}>✓ {row.new_style_loaded} loaded</div>}
          {row.new_style_failed > 0 && <div className="text-danger"  style={{ fontSize: 11 }}>✗ {row.new_style_failed} failed</div>}
        </div>
      ),
    },
    {
      name: "Prefix",
      selector: (row) => (
        <div style={{ margin: 0, lineHeight: 1.5 }}>
          <div>{row.prefix_count ?? "—"} chunks</div>
          {row.prefix_loaded > 0 && <div className="text-success" style={{ fontSize: 11 }}>✓ {row.prefix_loaded} loaded</div>}
          {row.prefix_failed > 0 && <div className="text-danger"  style={{ fontSize: 11 }}>✗ {row.prefix_failed} failed</div>}
        </div>
      ),
    },
    {
      name: "Dateless",
      selector: (row) => (
        <div style={{ margin: 0, lineHeight: 1.5 }}>
          <div>{row.dateless_count ?? "—"} chunks</div>
          {row.dateless_loaded > 0 && <div className="text-success" style={{ fontSize: 11 }}>✓ {row.dateless_loaded} loaded</div>}
          {row.dateless_failed > 0 && <div className="text-danger"  style={{ fontSize: 11 }}>✗ {row.dateless_failed} failed</div>}
        </div>
      ),
    },
    {
      name: "Open Sale",
      selector: (row) => (
        <div style={{ margin: 0, lineHeight: 1.5 }}>
          <div>{row.open_sale_count ?? "—"} rows</div>
        </div>
      ),
      width: "110px",
    },
    {
      name: "New Releases",
      selector: (row) => (
        <div style={{ margin: 0, lineHeight: 1.5 }}>
          <div>{row.new_releases_count ?? "—"} rows</div>
        </div>
      ),
      width: "120px",
    },
    {
      name: "Deleted",
      selector: (row) => (
        <div style={{ margin: 0, lineHeight: 1.5 }}>
          <div>{row.deleted_count ?? "—"} rows</div>
        </div>
      ),
      width: "100px",
    },
    {
      name: "Actions",
      width: "180px",
      selector: (row) => (
        <div className="dvla-actions" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {/* Live Logs */}
          <button 
            onClick={() => openLogs(row)} 
            className="action-btn logs" 
            title="View live process logs"
          >
            <HiClipboardDocumentList />
          </button>

          {/* Retry Extraction */}
          {row.extracted_status === "failed" && (
            <button 
              onClick={() => retryPipeline(row.id)} 
              className="action-btn retry" 
              title="Retry Extraction"
            >
              <HiArrowPath />
            </button>
          )}

          {/* Delete ZIP only */}
          {row.file_path && (
            <button 
              onClick={() => deleteZip(row.id)} 
              className="action-btn zip" 
              title="Delete ZIP only"
            >
              <HiInbox />
            </button>
          )}

          {/* Delete everything */}
          <button 
            onClick={() => deleteAllFiles(row.id)} 
            className="action-btn delete" 
            title="Delete ZIP + all splits"
          >
            <HiTrash />
          </button>
        </div>
      ),
    },
  ];

  // ─── Lifecycle ─────────────────────────────────────────────────────────────
  useEffect(() => {
    getHistory();
    document.title = "DVLA Management | JDM";
  }, []);

  // Auto-scroll log panel to bottom when new rows arrive
  useEffect(() => {
    if (logOpen) logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logRows]);

  // Cleanup poll on unmount
  useEffect(() => () => stopPolling(), []);

  // ─── API calls ─────────────────────────────────────────────────────────────
  async function getHistory() {
    try {
      let res = await apiRequest.get("/dvla-data/history");
      if (res.status === 200) setHistory(res.data.message || []);
    } catch (e) {
      console.log(e);
    }
  }

  async function promoteLive() {
    Swal.fire({
      title: "Promote Clones to Live?",
      text: "This will rename _clone tables to _live and build missing indexes. This operation runs in the background.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#27ae60",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Promote Now",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          let res = await apiRequest.post("/dvla-data/promote-live");
          Swal.fire({
            icon: "success",
            title: "Promotion Started",
            text: res.data.message || "Check Global Logs for progress.",
          });
          getHistory();
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Promotion Failed",
            text: error.response?.data?.message || "Failed to start promotion.",
          });
        }
      }
    });
  }

  async function fetchProcesses(jobId) {
    try {
      let res = await apiRequest.get(`/dvla-data/processes/${jobId}`);
      if (res.status === 200) setLogRows(res.data.message || []);
    } catch (e) { console.log(e); }
  }

  // ─── Log Viewer ────────────────────────────────────────────────────────────
  function openLogs(row) {
    setLogJobId(row.id);
    setLogJobName(row.filename);
    setLogRows([]);
    setLogOpen(true);

    fetchProcesses(row.id);

    // If job is still actively running, start polling every 3 seconds
    const isLive = row.status === "downloading" || row.extracted_status === "extracting";
    if (isLive) {
      startPolling(row.id);
    }
  }

  function startPolling(jobId) {
    setLogPolling(true);
    pollRef.current = setInterval(() => {
      fetchProcesses(jobId);
      // Also refresh the main history table
      getHistory();
    }, 3000);
  }

  function stopPolling() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setLogPolling(false);
  }

  function closeLogs() {
    stopPolling();
    setLogOpen(false);
    setLogRows([]);
  }

  // ─── Download ──────────────────────────────────────────────────────────────
  async function fetchNewSnapshot() {
    Swal.fire({
      title: "Trigger New Download?",
      text: `Will fetch a ~2.5GB DVLA zip and chunk into ${linesPerFile} lines each.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Start Pipeline",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          let res = await apiRequest.post("/dvla-data/fetch", { linesPerFile });
          Swal.fire({ icon: "success", title: "Download Started", text: res.data.message });
          getHistory();
        } catch {
          Swal.fire({ icon: "error", title: "Oops...", text: "Failed to trigger the download pipeline." });
        }
        setLoading(false);
      }
    });
  }

  // ─── Retry ─────────────────────────────────────────────────────────────────
  async function retryPipeline(id) {
    Swal.fire({
      title: "Retry Extraction?",
      text: "Clone tables will be wiped and re-populated from the existing ZIP.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#f39c12",
      cancelButtonColor: "#d33",
      confirmButtonText: "Retry Pipeline",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          let res = await apiRequest.post(`/dvla-data/retry/${id}`);
          Swal.fire({ icon: "success", title: "Retry Initiated", text: res.data.message });
          getHistory();
        } catch (error) {
          Swal.fire({ icon: "error", title: "Unavailable", text: error.response?.data?.message || "File might be missing." });
        }
      }
    });
  }

  // ─── Deletes ───────────────────────────────────────────────────────────────
  async function deleteZip(id) {
    Swal.fire({
      title: "Delete ZIP file?",
      text: "Removes only the downloaded ZIP. Splits and extracted files are kept.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e67e22",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, delete ZIP",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await apiRequest.delete(`/dvla-data/zip/${id}`);
          Swal.fire({ icon: "success", title: "ZIP Deleted", showConfirmButton: false, timer: 1500 });
          getHistory();
        } catch (error) {
          Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Could not delete ZIP." });
        }
      }
    });
  }

  async function deleteAllFiles(id) {
    Swal.fire({
      title: "Delete ALL files?",
      text: "Permanently removes the ZIP, extracted TXTs, and all split chunk folders.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c0392b",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, delete everything",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await apiRequest.delete(`/dvla-data/files/${id}`);
          Swal.fire({ icon: "success", title: "All Files Deleted", text: res.data.message, showConfirmButton: false, timer: 2000 });
          getHistory();
        } catch (error) {
          Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Could not delete files." });
        }
      }
    });
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="dvla-management-container">
      <style>{`
        .dvla-actions .action-btn {
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #fff;
          font-size: 18px;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          padding: 0;
          cursor: pointer;
        }
        .dvla-actions .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }
        .dvla-actions .action-btn.logs { color: #3b82f6; }
        .dvla-actions .action-btn.logs:hover { background: #eff6ff; border-color: #bfdbfe; }
        
        .dvla-actions .action-btn.retry { color: #f59e0b; }
        .dvla-actions .action-btn.retry:hover { background: #fffbeb; border-color: #fde68a; }
        
        .dvla-actions .action-btn.zip { color: #6366f1; }
        .dvla-actions .action-btn.zip:hover { background: #eef2ff; border-color: #c7d2fe; }
        
        .dvla-actions .action-btn.delete { color: #ef4444; }
        .dvla-actions .action-btn.delete:hover { background: #fef2f2; border-color: #fecaca; }

        .dvla-management-container .rdt_TableHeadRow {
          background-color: #f8fafc !important;
          border-top: 1px solid #e2e8f0;
        }
        .dvla-management-container .rdt_TableHeadRow div {
          font-weight: 700 !important;
          color: #475569 !important;
          font-size: 13px !important;
          text-transform: uppercase;
          letter-spacing: 0.025em;
        }

        .dvla-workflow {
          display: flex;
          justify-content: space-between;
          gap: 15px;
          margin-bottom: 30px;
          background: #fff;
          padding: 24px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .workflow-step {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          position: relative;
        }
        .workflow-step:not(:last-child)::after {
          content: "→";
          position: absolute;
          right: -12%;
          top: 15px;
          color: #cbd5e1;
          font-size: 20px;
        }
        .step-icon {
          width: 44px;
          height: 44px;
          background: #f1f5f9;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
          color: #3b82f6;
          font-size: 22px;
          transition: all 0.3s ease;
        }
        .workflow-step:hover .step-icon {
          background: #3b82f6;
          color: #fff;
          transform: translateY(-3px);
        }
        .step-title {
          font-weight: 700;
          font-size: 13px;
          color: #1e293b;
          margin-bottom: 4px;
          text-transform: uppercase;
        }
        .step-desc {
          font-size: 11px;
          color: #64748b;
          line-height: 1.4;
          max-width: 140px;
        }

        .admin-guide {
          background: #f8fafc;
          border-left: 4px solid #3b82f6;
          padding: 16px 20px;
          border-radius: 8px;
          margin-bottom: 20px;
          margin-top: 10px;
        }
        .admin-guide h4 {
          margin: 0 0 10px 0;
          font-size: 15px;
          color: #0f172a;
          font-weight: 700;
        }
        .admin-guide ol {
          margin: 0;
          padding-left: 20px;
          font-size: 14px;
          color: #334155;
          line-height: 1.6;
        }
        .admin-guide .danger-text {
          margin-top: 12px;
          font-size: 13px;
          color: #b91c1c;
          background: #fef2f2;
          padding: 8px 12px;
          border-radius: 6px;
          display: inline-block;
          font-weight: 600;
        }

      `}</style>
      <div className="main-content">

        {/* Header + Controls */}
        <div className="row">
          <div className="table-title selet ">
            <h2>DVLA Background Management</h2>
            <div className="search-list" style={{ display: "flex", gap: "15px", alignItems: "center" }}>
              <div>
                <label style={{ fontSize: "12px", opacity: 0.7, display: "block", marginBottom: "2px" }}>Lines Per Subfile</label>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: "120px" }}
                  value={linesPerFile}
                  onChange={(e) => setLinesPerFile(e.target.value)}
                />
              </div>
              <div style={{ alignSelf: "flex-end", paddingBottom: "3px", display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => openLogs({ id: 0, filename: "Global System Operations" })} className="btn btn-info">
                  Global Logs
                </button>
                <button type="button" onClick={promoteLive} className="btn btn-success">
                  Promote to Live
                </button>
                <button type="button" onClick={fetchNewSnapshot} className="btn btn-primary" disabled={loading}>
                  {loading ? "Triggering..." : "Download New Snapshot"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Instructions */}
        <div className="admin-guide">
          <h4>Admin Guide: How to update live plates</h4>
          <ol>
            <li><strong>Start Update:</strong> Click the <span style={{color: '#fff', background: '#0d6efd', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold'}}>Download New Snapshot</span> button to begin fetching the latest DVLA database.</li>
            <li><strong>Wait for Processing:</strong> Wait until the <strong>Download</strong> and <strong>Pipeline</strong> columns both show as <span className="text-success" style={{fontWeight: 'bold'}}>Completed</span> for your new job. You can click the <strong>Logs</strong> icon to monitor real-time progress.</li>
            <li><strong>Go Live:</strong> Once the pipeline is 100% completed, click <span style={{color: '#fff', background: '#198754', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold'}}>Promote to Live</span> to safely swap out the old data and optimize search performance.</li>
          </ol>
          <div className="danger-text">
            ⚠️ Important rules: Do NOT click "Promote to Live" while a download or extraction is still actively running. Do NOT close the Global Logs until all indexes are finished building.
          </div>
        </div>

        {/* System Workflow Guide */}
        <div className="dvla-workflow">
          <div className="workflow-step">
            <div className="step-icon"><HiCloudArrowDown /></div>
            <div className="step-title">1. Download</div>
            <div className="step-desc">Fetch fresh 2.5GB ZIP snapshot from DVLA API.</div>
          </div>
          <div className="workflow-step">
            <div className="step-icon"><HiDocumentDuplicate /></div>
            <div className="step-title">2. Extract</div>
            <div className="step-desc">Unzip and prepare raw data files for processing.</div>
          </div>
          <div className="workflow-step">
            <div className="step-icon"><HiScissors /></div>
            <div className="step-title">3. Chunk</div>
            <div className="step-desc">Split millions of rows into manageable subfiles.</div>
          </div>
          <div className="workflow-step">
            <div className="step-icon"><HiArrowUpOnSquareStack /></div>
            <div className="step-title">4. Load</div>
            <div className="step-desc">High-speed bulk ingestion into _clone tables.</div>
          </div>
          <div className="workflow-step">
            <div className="step-icon"><HiRocketLaunch /></div>
            <div className="step-title">5. Promote</div>
            <div className="step-desc">Swap to _live tables and build optimized indexes.</div>
          </div>
        </div>

        {/* History Table */}
        <div className="row mt-4">
          <div className="col-md-12">
            <div className="datatable0">
              <DataTable columns={columns} data={history} pagination highlightOnHover />
            </div>
          </div>
        </div>

      </div>

      {/* ── Live Log Viewer Modal ─────────────────────────────────────────────── */}
      {logOpen && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.55)", display: "flex",
          alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#1a1a2e", borderRadius: "12px", width: "85vw", maxWidth: "900px",
            maxHeight: "80vh", display: "flex", flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          }}>
            {/* Modal header */}
            <div style={{
              padding: "16px 20px", borderBottom: "1px solid #2e2e4e",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div>
                <h5 style={{ margin: 0, color: "#fff" }}>
                  Process Logs — Job #{logJobId}
                </h5>
                <small style={{ color: "#888", wordBreak: "break-all" }}>{logJobName}</small>
              </div>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                {logPolling && (
                  <span style={{ color: "#3498db", fontSize: "13px" }}>
                    ● Live — refreshing every 3s
                  </span>
                )}
                {logPolling ? (
                  <button className="btn btn-sm btn-secondary" onClick={stopPolling}>Pause</button>
                ) : (
                  <button className="btn btn-sm btn-info" onClick={() => startPolling(logJobId)}>Resume</button>
                )}
                <button className="btn btn-sm btn-danger" onClick={closeLogs}>✕ Close</button>
              </div>
            </div>

            {/* Log entries */}
            <div style={{ overflowY: "auto", flex: 1, padding: "12px 20px", fontFamily: "monospace" }}>
              {logRows.length === 0 ? (
                <p style={{ color: "#555", textAlign: "center", marginTop: "30px" }}>
                  No process entries yet. The pipeline may still be starting...
                </p>
              ) : (
                logRows.map((p) => (
                  <div key={p.id} style={{
                    display: "flex", alignItems: "flex-start", gap: "12px",
                    padding: "8px 0", borderBottom: "1px solid #1e1e3a",
                  }}>
                    {/* Status dot */}
                    <div style={{
                      width: 10, height: 10, borderRadius: "50%", marginTop: 5, flexShrink: 0,
                      background: statusColor(p.status),
                      boxShadow: p.status === "started" ? `0 0 6px ${statusColor(p.status)}` : "none",
                    }} />
                    <div style={{ flex: 1 }}>
                      {/* Stage + filename */}
                      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
                        <span style={{ color: "#ddd", fontSize: "13px", fontWeight: 600 }}>
                          {stageLabel(p.stage)}
                          {p.filename && <span style={{ color: "#888", fontWeight: 400 }}> · {p.filename}</span>}
                        </span>
                        <span style={{ color: statusColor(p.status), fontSize: "12px", textTransform: "capitalize" }}>
                          {p.status}
                          {p.rows_affected > 0 && <span style={{ color: "#888" }}> · {p.rows_affected.toLocaleString()} rows</span>}
                          {p.duration_sec > 0 && <span style={{ color: "#3498db" }}> · {p.duration_sec}s</span>}
                        </span>
                      </div>
                      {/* Error message */}
                      {p.error_message && (
                        <div style={{
                          marginTop: 4, padding: "4px 8px", background: "#2e0e0e",
                          border: "1px solid #5c1a1a", borderRadius: 4,
                          color: "#e74c3c", fontSize: "12px", wordBreak: "break-all",
                        }}>
                          {p.error_message}
                        </div>
                      )}
                      {/* Timestamps */}
                      <div style={{ color: "#555", fontSize: "11px", marginTop: 2 }}>
                        {p.started_at && <>Started: {new Date(p.started_at).toLocaleTimeString()}</>}
                        {p.ended_at   && <> · Ended: {new Date(p.ended_at).toLocaleTimeString()}</>}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={logEndRef} />
            </div>

            {/* Footer summary */}
            <div style={{
              padding: "10px 20px", borderTop: "1px solid #2e2e4e",
              display: "flex", justifyContent: "space-between", color: "#666", fontSize: "12px",
            }}>
              <span>{logRows.length} events logged · Total Time: {logRows.reduce((acc, r) => acc + (r.duration_sec || 0), 0)}s</span>
              <span>
                ✓ {logRows.filter((r) => r.status === "completed").length} completed ·
                ● {logRows.filter((r) => r.status === "started").length} running ·
                ✗ {logRows.filter((r) => r.status === "failed").length} failed
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dvla;
